import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProjectNavComponent } from '../../projects/project-nav/project-nav.component';
import { PageHeaderComponent } from '../../components/page-header/page-header.component';
import { SearchBarComponent } from '../../components/search-bar/search-bar.component';
import { ObjectMappingService } from '../../services/object-mapping.service';
import { ProjectRoleService } from '../../services/project-role.service';
import { StatusKind, StatusPillComponent } from '../../components/status-pill/status-pill.component';
import { ObjectMappingCreateDialogComponent } from './object-mapping-create-dialog/object-mapping-create-dialog.component';
import { ObjectMappingSummary } from '../../models/object-mapping.model';

interface LibraryState {
  kind: StatusKind;
  key: string;
  count: number;
}

/** Eine Pille je Bibliothek: brüchig vor nie getroffen; unbenutzt und verschwunden zaehlen nicht. */
export function libraryState(library: ObjectMappingSummary): LibraryState | null {
  if (library.entryCount === 0) return null;
  const fragile = library.fragileCount ?? 0;
  if (fragile > 0) return { kind: 'pending', key: 'objectMappings.state.pill.fragile', count: fragile };
  const neverHit = library.neverHitCount ?? 0;
  if (neverHit > 0) return { kind: 'inaktiv', key: 'objectMappings.state.pill.neverHit', count: neverHit };
  return { kind: 'aktiv', key: 'objectMappings.state.pill.allHit', count: 0 };
}

/**
 * Liste der Objektbibliotheken eines Projekts (NEX-1276).
 *
 * Design-Luecke: Das Bundle enthaelt keinen Entwurf fuer diese Seite. Aufbau folgt der
 * Testdatenblatt-Liste (Kopfzeile, Suchleiste, Kartentabelle). Liegt ein Entwurf vor,
 * gehoert die Seite angeglichen. Die Spalte „Herkunft" fehlt, weil die API kein Feld dafuer
 * liefert.
 */
@Component({
  selector: 'app-object-mapping-list',
  imports: [
    TranslatePipe,
    MatButton,
    MatIcon,
    MatProgressSpinner,
    ProjectNavComponent,
    PageHeaderComponent,
    SearchBarComponent,
    StatusPillComponent,
    ObjectMappingCreateDialogComponent,
  ],
  templateUrl: './object-mapping-list.component.html',
  styleUrl: './object-mapping-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ObjectMappingListComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(ObjectMappingService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isProjectReader = inject(ProjectRoleService).isProjectReader;

  readonly projectId: string = this.route.snapshot.params['projectId'];

  readonly loading = signal(true);
  readonly loadError = signal(false);
  readonly searchTerm = signal('');
  readonly libraries = signal<ObjectMappingSummary[]>([]);
  readonly creating = signal(false);
  readonly stateOf = libraryState;

  readonly filtered = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const all = this.libraries();
    return term ? all.filter((lib) => lib.name.toLowerCase().includes(term)) : all;
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.loadError.set(false);
    this.service
      .listAll(this.projectId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (all) => {
          this.libraries.set(all);
          this.loading.set(false);
        },
        error: () => {
          this.libraries.set([]);
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  open(library: ObjectMappingSummary): void {
    this.router.navigate(['/projects', this.projectId, 'object-mappings', library.id]);
  }

  onCreated(library: ObjectMappingSummary): void {
    this.creating.set(false);
    this.open(library);
  }
}
